import { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import { Button } from "react-bootstrap";

import { CircleCheckIcon } from "../../../shared/icons/Icons";
import { TextAreaInput } from "../../../shared/form/FormInput";
import { RenderQuestion } from "../take-assessments/QuestionRenderer";
import { QUESTION_TYPE } from "../../../shared/utils/constants";
import { getBase64Content } from "../../../utils/utils";
import { deleteApi, getApi, postApi, putApi } from "../../../shared/services/apiHelper";
import { assignmentSubmissionAssessmentDetail, fileAssemble, fileUpload, fileUploadFragment, runStatusUpdate, takeAssessmentRunDeleteAsset } from "../../../shared/settings/apiConfig";

import "./AssignmentAnalaysis.scss";

export const uploadLargetAsset = async (asset, progressCallback = null) => {
    try {
        const fileName = asset.name;
        const fileSize = asset.size;

        let offset = 0;
        let chunkCount = 0;
        const chunkSize = Math.ceil(1024 * 1024 * 2); // equals to 2mb
        const totalChunks = Math.ceil(fileSize / chunkSize, chunkSize);

        const fileUploadPayload = {
            filename: fileName,
            size: fileSize,
            partCount: totalChunks
        };

        progressCallback && progressCallback({ uploading: true, progress: 0 });

        let response = await postApi(fileUpload, { data: fileUploadPayload });
        if (response.data.status === 'success') {
            const partKey = response.data.partkey;

            while (chunkCount < totalChunks) {
                offset = chunkCount * chunkSize;

                const fileUploadFragmentPayload = {
                    partkey: partKey,
                    partid: chunkCount + 1,
                    content: await getBase64Content(asset.file.slice(offset, offset + chunkSize)),
                };

                response = await postApi(fileUploadFragment, { data: fileUploadFragmentPayload });

                if (response.data.status === 'success') {
                    chunkCount++;

                    let progress = ((chunkCount / totalChunks) * 100).toFixed(0);
                    progressCallback && progressCallback({ uploading: true, progress: progress == 100 ? 99 : progress });
                } else {
                    response = { status: false, message: 'Error while uploading' };
                    break;
                }
            }

            if (chunkCount === totalChunks) {
                let convertedStringName = fileName.replace(/\u202F/g, " ");
                asset.image = btoa(`BIG${convertedStringName}$${partKey}$${fileSize}$${totalChunks}`);
                response = { status: true, message: response.data.message };

                progressCallback && progressCallback({ uploading: false, progress: 99 });
            }

        } else {
            response = { status: false, message: response.data.message };
        }

        return response;
    } catch (error) {
        return { status: false, message: 'Error while uploading' };
    }
};

const AssignmentAnalysis = (props) => {

    const { assignmentId } = useParams();

    const [assessmentData, setAssessmentData] = useState({});
    const [fileName, setFileName] = useState("");
    const [constModal, setConstModal] = useState({ show: false });

    useEffect(() => {
        getApi(assignmentSubmissionAssessmentDetail + assignmentId).then(response => {
            setAssessmentData(response.data.data);
        }).catch(error => {
            console.log(error);
        })
    }, []);

    const openFolder = () => {
        let input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,'

        input.onchange = async (event) => {
            if (event.target.files?.length > 0) {
                onChangeFile(event.target.files[0]);
            }
        }

        input.click();
    };

    const onChangeFile = async (file) => {
        const asset = { name: file.name, size: file.size, file };
        setFileName(file.name)

        let response = await uploadLargetAsset(asset);

        if (response.status) {

            delete asset.file;

            postApi(fileAssemble, { data: asset }).then(response => {
                setConstModal(prevState => {
                    prevState.data.pdf = response.data.data.url;
                    prevState.data.rawpdf = file;
                    prevState.data.error = "";
                    return { ...prevState };
                });
            }).catch(error => {
                console.log(error);
            });
        }
    }

    const handleSubmit = () => {
        let questions = [];
        assessmentData.sections.forEach(section => {
            section.questions.forEach(question => {
                let qData = { questionname: question.questionname, questiontype: question.questiontype };

                let answer = question.answerData.answer;

                if ( Array.isArray(answer) ) {
                    qData.options = question.options.filter(option => answer.includes(option.id)).map(option => option.optionname);
                } else if ( qData.questiontype === QUESTION_TYPE.DESCRIPTIVE || qData.questiontype === QUESTION_TYPE.CODING ) {
                    qData.singleinputanswer = answer;
                    qData.language = question.answerData.language;
                } else if ( qData.questiontype === QUESTION_TYPE.RATING ) {
                    qData.ratinganswer = answer;
                }
                
                if ( Array.isArray(question.attachmentsData) && question.attachmentsData.length > 0 ) {
                    qData.attachments = question.attachmentsData.map(elem => {
                        return { name: elem.name, type: elem.type, url: elem.serverurl };
                    });
                }

                questions.push(qData);
            });
        });

        let payload = {
            questions: questions,
            pdf_file: constModal.data?.pdf || null,
            runid: assignmentId
        };

        putApi(runStatusUpdate, { data: payload }).then(response => {
            props.history.push("/assignments");
        }).catch(error => {
            console.log(error);
        });
    };

    const deleteAnswerAsset = (asset, index, isUploaded = false, sectionIndex, questionIndex) => {
        // TODO have a separate delete api for this
        const payload = {};
        if (asset.id) {
            payload.url = asset.url;
        } else {
            payload.url = asset.serverurl;
        }

        deleteApi(takeAssessmentRunDeleteAsset, { data: payload }).then(response => {
            setAssessmentData(prevState => {
                prevState.sections[sectionIndex].questions[questionIndex].attachmentsData.splice(index, 1);
                return {...prevState};
            });
        }).catch((error) => {
            console.log(error);
        });
    };

    return (
        <div className="main-content assignment-container">
            <h4 className="fw-bold">Assignment Analysis</h4>
            <div className="">
                <div className="d-flex align-items-center gap-2">
                    <CircleCheckIcon size={24} color="hsl(270 60% 45%)" />
                    <span className="ml-2 fw-bold">{assessmentData.assessmentname}</span>
                </div>
                <p className="mt-2">Please answer the following questions about your assignment experience</p>
                <div className="d-flex flex-column gap-4 mt-3">
                    {
                        Array.isArray(assessmentData.sections) && assessmentData.sections.map((section, sectionIndex) => (
                            <div key={'section-' + section.id}>
                                <h4>{section.sectionname}</h4>
                                <h2>{section.sectionsummary}</h2>
                                <div className="d-flex flex-column gap-4">
                                    {
                                        Array.isArray(section.questions) && section.questions.map((question, questionIndex) => (
                                            <div className="question-container" key={question.id}>
                                                <RenderQuestion 
                                                    question={question}
                                                    questionIndex={questionIndex}
                                                    inlineAttachment={true}
                                                    lineBreak={false}
                                                    attachments={question.attachmentsData || []}
                                                    value={question.answerData || { answer: null, previousAnswer: null, language: null }}
                                                    onChange={(data) => {
                                                        setAssessmentData(prevState => {
                                                            prevState.sections[sectionIndex].questions[questionIndex].answerData = data;
                                                            return {...prevState};
                                                        });
                                                    }}
                                                    assetCallback={(data) => {
                                                        setAssessmentData(prevState => {
                                                            prevState.sections[sectionIndex].questions[questionIndex].attachmentsData = data;
                                                            return {...prevState};
                                                        });
                                                    }}
                                                    deleteAssetCallback={(asset, index, isUploaded) => {
                                                        deleteAnswerAsset(asset, index, isUploaded, sectionIndex, questionIndex);
                                                    }}
                                                />
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        ))
                    }

                    {/* <div className="question">
                        <label>Upload your assignment file (optional)</label>
                        <div className="question-container gap-3 d-flex">
                            <div className="input-section" style={{ marginLeft: "0%" }}>
                                <Form className="run-type-controls d-flex">
                                    {
                                        <><Form.Control type="text" style={{ borderRadius: '5px 0px 0px 5px', width: '65%' }} disabled value={fileName} /><button onClick={(e) => {
                                            e.preventDefault();
                                            openFolder()
                                            setConstModal(prevState => {
                                                return {
                                                    ...prevState, data: {
                                                        ...prevState.data,
                                                        error: ""
                                                    }
                                                }
                                            })
                                        }} className="file-upload-btn" >Browse File</button></>
                                    }
                                </Form>
                            </div>
                        </div>
                    </div> */}

                    {/* <div className="question">
                        <label>7. Additional Comments</label>
                        <TextAreaInput type="text" name="comments" placeholder="Enter your comments here..." className="mb-3" isMandatory={false} value={formData.comments} onChange={handleTextAreaChange} />
                    </div> */}
                </div>
            </div>
            <div className="d-flex align-items-center justify-content-center">
                <Button variant="primary" type="submit" className="submit-btn" onClick={() => {handleSubmit()}}>Submit</Button>
            </div>
        </div>
    );
};

export default AssignmentAnalysis;